import { trackAdActivity } from "../utils/adAnalytics";
import { useState, useRef, useEffect, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import paper from "paper";
import "./Design.css";
import { useModal } from "../context/ModalContext";
import { DESIGN_SAVE_LIMIT, STORAGE_RETENTION_DAYS } from "../config/features";
import { ClosedVsBrokenDiagram } from "./Guide/GuideMockups";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// 저장 안 한 작업 내용을 탭 세션 동안 보관 (다른 페이지 다녀와도 유지)
const DRAFT_KEY = "cookistory_design_draft";
// sessionStorage는 보통 5MB 남짓. 넉넉히 남겨두고 넘치면 초안을 포기한다.
const DRAFT_MAX_CHARS = 3_000_000;
// 탭을 며칠씩 열어두는 경우 대비. 오래된 초안은 되살리지 않고 지운다.
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
// 저장 버튼을 안 누른 디자인(임시저장) 보관 기간
const DRAFT_RETENTION_DAYS = 7;
const CANVAS_MARGIN = 48; // 그림판 종이 둘레 여백(px)
const DRAFT_RETENTION_MS = DRAFT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
// 굵기를 줄여도 획이 사라지지 않도록 남겨두는 최소 굵기(px)
const MIN_STROKE_WIDTH = 0.5;

export default function DesignPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { showConfirm, showAlert } = useModal();
  const location = useLocation();
  const canvasRef = useRef(null);
  const paperScopeRef = useRef(null);

  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [converting, setConverting] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [tool, setTool] = useState("select"); // select | node | pen | line | circle | rect
  const toolRef = useRef("select");
  const [history, setHistory] = useState([]);
  // 같은 프롬프트를 연속으로 몇 번째 생성 중인지 (서버 변주 지시문 선택용)
  const attemptRef = useRef({ prompt: "", count: 0 });
  // 초안 보존: 복원이 끝나기 전에는 저장하지 않는다 (빈 캔버스로 덮어쓰기 방지)
  const draftRestoredRef = useRef(false);
  const draftTimerRef = useRef(null);
  // 생성 중 취소용
  const generateAbortRef = useRef(null);
  const [error, setError] = useState("");
  const [aiUsage, setAiUsage] = useState(null); // { used, limit, plan }
  const [strokeWidth, setStrokeWidth] = useState(7);
  // 이미 그려진 그림의 굵기 보정값 (0 = 원본, + 굵게 / - 얇게)
  const [thicknessDelta, setThicknessDelta] = useState(0);
  const thicknessEditingRef = useRef(false);

  // Undo/Redo
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [canvasReady, setCanvasReady] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [drawMode, setDrawMode] = useState("ai"); // "ai" | "hand"
  const [introDismissed, setIntroDismissed] = useState(false);
  const promptRef = useRef(null);
  // 저장 버튼을 누르지 않은 작업 (생성 시 자동저장분) — 7일 뒤 자동 삭제
  const [draftDesigns, setDraftDesigns] = useState([]);
  const [activeDesignId, setActiveDesignId] = useState(null);
  const [renameModal, setRenameModal] = useState({ open: false, id: null, name: "" });
  const [selectedDesignIds, setSelectedDesignIds] = useState([]);
  const [saveModal, setSaveModal] = useState({ open: false, name: "" });

  // AI 사용량 조회 (비회원도 IP 기반 카운트 표시)
  const fetchUsage = useCallback(async () => {
    try {
      const url = user?.id ? `/ai/usage?user_id=${user.id}` : `/ai/usage`;
      const res = await fetch(url);
      if (res.ok) setAiUsage(await res.json());
    } catch {}
  }, [user?.id]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // 저장된 디자인 목록 조회
  const fetchSavedDesigns = useCallback(async () => {
    if (!user?.id) return;

    // 기간 지난 임시저장 정리 (목록을 열 때마다 한 번씩)
    const cutoff = new Date(Date.now() - DRAFT_RETENTION_MS).toISOString();
    await supabase
      .from("designs")
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .eq("is_draft", true)
      .lt("updated_at", cutoff);

    const run = (cols) =>
      supabase
        .from("designs")
        .select(cols)
        .eq("user_id", user.id)
        .or("is_deleted.is.null,is_deleted.eq.false")
        .order("updated_at", { ascending: false });

    let { data, error } = await run("id, name, thumbnail, updated_at, is_draft");
    if (error) {
      // is_draft 컬럼이 아직 없는 환경 — 목록이 통째로 안 뜨는 것보다 낫다
      console.warn("[디자인] is_draft 조회 실패, 임시저장 구분 없이 표시", error.message);
      ({ data } = await run("id, name, thumbnail, updated_at"));
    }
    if (!data) return;
    setSavedDesigns(data.filter((d) => !d.is_draft));
    setDraftDesigns(data.filter((d) => d.is_draft));
  }, [user?.id]);

  useEffect(() => { fetchSavedDesigns(); }, [fetchSavedDesigns]);

  /* 초안 보존 — 이 두 effect는 반드시 아래 캔버스 초기화 effect보다 먼저 정의해야 한다.
     React는 정의한 순서대로 cleanup을 실행하는데, 캔버스 cleanup이 project를 지워버려서
     그 뒤에 기록하면 빈 캔버스가 저장된다. */
  const draftMetaRef = useRef({ prompt: "", activeDesignId: null });
  useEffect(() => {
    draftMetaRef.current = { prompt, activeDesignId };
  });
  useEffect(
    () => () => {
      clearTimeout(draftTimerRef.current);
      writeDraftNow(); // 대기 중이던 저장을 떠나기 전에 확정
    },
    []
  );

  // Paper.js 초기화
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const wrap = canvas.parentElement;

    // 초기 canvas 해상도 설정 (정사각형)
    const rect = wrap.getBoundingClientRect();
    // 리뉴얼: 종이 둘레에 여백(CANVAS_MARGIN)을 두어 점무늬 바탕이 보이게 한다
    const size = Math.max(200, Math.floor(Math.min(rect.width, rect.height)) - CANVAS_MARGIN);
    canvas.width = size;
    canvas.height = size;
    canvas.style.width = size + "px";
    canvas.style.height = size + "px";

    const scope = new paper.PaperScope();
    scope.setup(canvas);
    paperScopeRef.current = scope;
    setCanvasReady(true);

    // 리사이즈 대응 (정사각형 유지)
    const onResize = () => {
      const r = wrap.getBoundingClientRect();
      const s = Math.max(200, Math.floor(Math.min(r.width, r.height)) - CANVAS_MARGIN);
      canvas.style.width = s + "px";
      canvas.style.height = s + "px";
      scope.view.viewSize = new scope.Size(s, s);
    };
    window.addEventListener("resize", onResize);
    // 굵기 막대가 나타나고 사라질 때도 칸 높이가 바뀐다 → 다시 잰다
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => onResize()) : null;
    ro?.observe(wrap);

    // 미들 마우스 또는 스페이스+드래그로 뷰 패닝
    let isPanning = false;
    let panStart = null;
    let spaceDown = false;

    const onKeyDownPan = (e) => { if (e.code === "Space") spaceDown = true; };
    const onKeyUpPan = (e) => { if (e.code === "Space") spaceDown = false; };
    window.addEventListener("keydown", onKeyDownPan);
    window.addEventListener("keyup", onKeyUpPan);

    const onPanDown = (e) => {
      if (e.button === 1 || (spaceDown && e.button === 0)) {
        isPanning = true;
        panStart = new scope.Point(e.offsetX, e.offsetY);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onPanMove = (e) => {
      if (isPanning && panStart) {
        const cur = new scope.Point(e.offsetX, e.offsetY);
        const delta = cur.subtract(panStart);
        scope.view.center = scope.view.center.subtract(delta.divide(scope.view.zoom));
        panStart = cur;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onPanUp = () => {
      isPanning = false;
      panStart = null;
    };

    canvas.addEventListener("mousedown", onPanDown);
    canvas.addEventListener("mousemove", onPanMove);
    canvas.addEventListener("mouseup", onPanUp);

    // Ctrl+휠 확대/축소
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const viewPos = scope.view.viewToProject(
        new scope.Point(e.offsetX, e.offsetY)
      );
      scope.view.scale(delta, viewPos);
    };
    canvasRef.current.addEventListener("wheel", onWheel, { passive: false });

    // 글로벌 키보드 핸들러 (입력 필드에서는 무시)
    const onKeyDown = (e) => {
      const tag = e.target.tagName;
      const isTextInput = tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable;

      // Ctrl+Z/Y: 캔버스 undo/redo를 항상 우선 처리
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }

      // 텍스트 입력 중이면 나머지 단축키는 무시
      if (isTextInput) return;

      // 노드 도구일 때는 Delete/Backspace를 Paper.js onKeyDown에 맡김
      if (toolRef.current === "node" && (e.key === "Delete" || e.key === "Backspace")) return;

      if ((e.key === "Delete" || e.key === "Backspace") && scope.project.selectedItems.length) {
        saveUndo();
        scope.project.selectedItems.forEach((item) => item.remove());
        updateHasContent();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keydown", onKeyDownPan);
      window.removeEventListener("keyup", onKeyUpPan);
      canvasRef.current?.removeEventListener("wheel", onWheel);
      canvasRef.current?.removeEventListener("mousedown", onPanDown);
      canvasRef.current?.removeEventListener("mousemove", onPanMove);
      canvasRef.current?.removeEventListener("mouseup", onPanUp);
      scope.project?.clear();
    };
  }, []);

  // 선택 도구
  const setupSelectTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const selectTool = new scope.Tool();
    let selectedItem = null;
    let dragOffset = null;
    let selectionRect = null;
    let dragStart = null;
    let isDraggingSelection = false;

    selectTool.onMouseDown = (e) => {
      const hit = scope.project.hitTest(e.point, {
        segments: false,
        stroke: true,
        fill: true,
        tolerance: 8,
      });

      if (scope.project.selectedItems.length) {
        scope.project.deselectAll();
      }

      if (hit && hit.item && !hit.item.data?._eraser) {
        selectedItem = hit.item;
        selectedItem.selected = true;
        dragOffset = e.point.subtract(selectedItem.position);
        saveUndo();
      } else {
        // 빈 곳 클릭 → 드래그 영역 선택 시작
        selectedItem = null;
        dragStart = e.point;
        isDraggingSelection = true;
      }
    };

    selectTool.onMouseDrag = (e) => {
      if (selectedItem && dragOffset) {
        selectedItem.position = e.point.subtract(dragOffset);
      } else if (isDraggingSelection && dragStart) {
        if (selectionRect) selectionRect.remove();
        selectionRect = new scope.Path.Rectangle({
          from: dragStart,
          to: e.point,
          strokeColor: new scope.Color(0.4, 0.4, 1),
          strokeWidth: 1,
          dashArray: [4, 4],
          fillColor: new scope.Color(0.4, 0.4, 1, 0.08),
        });
      }
    };

    selectTool.onMouseUp = (e) => {
      if (isDraggingSelection && dragStart && selectionRect) {
        const rect = new scope.Rectangle(dragStart, e.point);
        selectionRect.remove();
        selectionRect = null;

        const allItems = [];
        function findItems(item) {
          if (item.className === "Path" || item.className === "CompoundPath" || item.className === "Group") {
            allItems.push(item);
          }
          if (item.children) item.children.forEach(findItems);
        }
        findItems(scope.project.activeLayer);

        allItems.forEach((item) => {
          if (item.bounds.intersects(rect)) {
            item.selected = true;
          }
        });
      }

      dragOffset = null;
      isDraggingSelection = false;
      dragStart = null;
    };

    selectTool.onKeyDown = (e) => {
      if (e.key === "delete" || e.key === "backspace") {
        const items = scope.project.selectedItems;
        if (items.length) {
          saveUndo();
          items.forEach((item) => item.remove());
          updateHasContent();
        }
      }
    };

    selectTool.activate();
  }, []);

  // 노드 편집 도구
  const setupNodeTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const nodeTool = new scope.Tool();
    // 여러 패스에 걸쳐 선택된 세그먼트 추적
    let selectedSegs = new Set();
    let dragSegment = null;
    let dragHandle = null;
    let selectionRect = null;
    let dragStart = null;
    let isDraggingSelection = false;
    let hasDragged = false;
    let lastClickTime = 0;
    let lastClickSeg = null;

    function clearSelection() {
      selectedSegs.forEach((seg) => { seg.selected = false; });
      selectedSegs = new Set();
      scope.project.deselectAll();
    }

    /* 클릭 판정 반경(화면 px). paper의 tolerance는 프로젝트 좌표라 줌을 타지 않아서,
       그대로 두면 확대할수록 판정 범위가 화면에서 커진다. 줌으로 나눠 맞춘다. */
    const HANDLE_PICK_PX = 12;
    const ANCHOR_PICK_PX = 12;
    const STROKE_PICK_PX = 8;
    const pickTolerance = (px) => px / (scope.view.zoom || 1);

    // 히트의 실제 좌표. 핸들은 앵커 기준 상대벡터라 앵커를 더해야 한다.
    function hitPointOf(hit) {
      if (hit.type === "stroke") return hit.location?.point || null;
      const seg = hit.segment;
      if (!seg) return null;
      if (hit.type === "handle-in") return seg.point.add(seg.handleIn);
      if (hit.type === "handle-out") return seg.point.add(seg.handleOut);
      return seg.point;
    }

    /* hitTest는 반경 안에서 "먼저 걸린" 하나만 준다. 앵커가 촘촘한 도안에서는
       클릭한 곳에서 가장 가까운 앵커가 아니라 엉뚱한 앵커가 잡힌다.
       hitTestAll로 후보를 다 받아 실제 거리로 다시 고른다. */
    function bestHit(hits, point, filter) {
      let best = null;
      let bestDist = Infinity;
      for (const h of hits || []) {
        if (h.item?.data?._eraser) continue;   // 지우개 임시 아이템
        if (h.item?.className !== "Path") continue;
        if (filter && !filter(h)) continue;
        const p = hitPointOf(h);
        if (!p) continue;
        const d = p.getDistance(point);
        if (d < bestDist) { bestDist = d; best = h; }
      }
      return { hit: best, dist: bestDist };
    }

    nodeTool.onMouseDown = (e) => {
      hasDragged = false;

      /* 선택된 앵커의 핸들. 화면에 떠 있는 작은 표적이라 앵커와 같은 자격으로 겨루게
         해야 한다. 예전처럼 "앵커를 못 찾았을 때만" 보면 옆 앵커에 밀려 영영 안 잡힌다.
         길이 0인 핸들(직선 구간)은 앵커와 좌표가 겹치므로 후보에서 뺀다. */
      const handleBest = selectedSegs.size
        ? bestHit(
            scope.project.hitTestAll(e.point, {
              segments: false,
              handles: true,
              stroke: false,
              tolerance: pickTolerance(HANDLE_PICK_PX),
            }),
            e.point,
            (h) =>
              h.segment &&
              selectedSegs.has(h.segment) &&
              (h.type === "handle-in" ? h.segment.handleIn : h.segment.handleOut).length > 0
          )
        : { hit: null, dist: Infinity };

      const anchorBest = bestHit(
        scope.project.hitTestAll(e.point, {
          segments: true,
          handles: false,
          stroke: false,
          tolerance: pickTolerance(ANCHOR_PICK_PX),
        }),
        e.point
      );

      // 더 가까운 쪽이 이긴다. 같은 거리면 핸들 우선(앵커 위에 그려져 있다).
      let hit = null;
      if (handleBest.hit && handleBest.dist <= anchorBest.dist) {
        hit = handleBest.hit;
      } else if (anchorBest.hit) {
        hit = anchorBest.hit;
      } else {
        // 앵커도 핸들도 없으면 선(stroke)
        hit = bestHit(
          scope.project.hitTestAll(e.point, {
            segments: false,
            handles: false,
            stroke: true,
            tolerance: pickTolerance(STROKE_PICK_PX),
          }),
          e.point
        ).hit;
      }

      dragSegment = null;
      dragHandle = null;

      if (hit) {
        const hitPath = hit.item.className === "Path" ? hit.item : null;

        if (hit.type === "segment" && hitPath) {
          const now = Date.now();
          const isDoubleClick = (now - lastClickTime < 400) && lastClickSeg === hit.segment;
          lastClickTime = now;
          lastClickSeg = hit.segment;

          if (isDoubleClick) {
            // 더블클릭: 핸들 토글 (직선 ↔ 곡선)
            saveUndo();
            const seg = hit.segment;
            const hasHandles = seg.handleIn.length > 0 || seg.handleOut.length > 0;
            if (hasHandles) {
              // 곡선 → 직선: 핸들 제거
              seg.handleIn = new scope.Point(0, 0);
              seg.handleOut = new scope.Point(0, 0);
            } else {
              // 직선 → 곡선: 핸들 자동 생성 (smooth)
              seg.smooth();
            }
            return;
          }

          if (e.modifiers.shift) {
            // Shift+클릭: 토글 (여러 패스 가능)
            if (selectedSegs.has(hit.segment)) {
              hit.segment.selected = false;
              selectedSegs.delete(hit.segment);
            } else {
              hit.segment.selected = true;
              selectedSegs.add(hit.segment);
            }
          } else {
            // 일반 클릭: 이미 선택된 포인트면 유지, 아니면 단일 선택
            if (!selectedSegs.has(hit.segment)) {
              clearSelection();
              hit.segment.selected = true;
              selectedSegs.add(hit.segment);
            }
          }
          dragSegment = hit.segment;
          saveUndo();

        } else if ((hit.type === "handle-in" || hit.type === "handle-out") && hitPath) {
          dragSegment = hit.segment;
          dragHandle = hit.type === "handle-in" ? "handleIn" : "handleOut";
          saveUndo();

        } else if (hit.type === "stroke" && hitPath) {
          // 선 위 클릭 → 새 포인트 추가
          saveUndo();
          clearSelection();
          const newSeg = hitPath.insert(hit.location.index + 1, e.point);
          newSeg.selected = true;
          selectedSegs.add(newSeg);
        }
      } else {
        // 빈 곳 클릭
        if (!e.modifiers.shift) {
          clearSelection();
        }
        dragStart = e.point;
        isDraggingSelection = true;
      }
    };

    nodeTool.onMouseDrag = (e) => {
      hasDragged = true;

      if (dragSegment) {
        if (dragHandle === "handleIn") {
          dragSegment.handleIn = e.point.subtract(dragSegment.point);
        } else if (dragHandle === "handleOut") {
          dragSegment.handleOut = e.point.subtract(dragSegment.point);
        } else {
          // 선택된 포인트들 모두 이동
          const delta = e.delta;
          selectedSegs.forEach((seg) => {
            seg.point = seg.point.add(delta);
          });
        }
      } else if (isDraggingSelection && dragStart) {
        if (selectionRect) selectionRect.remove();
        selectionRect = new scope.Path.Rectangle({
          from: dragStart,
          to: e.point,
          strokeColor: new scope.Color(0.4, 0.4, 1),
          strokeWidth: 1,
          dashArray: [4, 4],
          fillColor: new scope.Color(0.4, 0.4, 1, 0.08),
        });
      }
    };

    nodeTool.onMouseUp = (e) => {
      if (isDraggingSelection && dragStart && selectionRect && hasDragged) {
        const rect = new scope.Rectangle(dragStart, e.point);
        selectionRect.remove();
        selectionRect = null;

        if (!e.modifiers.shift) {
          clearSelection();
        }

        // 드래그 영역 안의 앵커포인트만 선택 (fullySelected 안 씀)
        const allPaths = [];
        function findPaths(item) {
          if (item.className === "Path") allPaths.push(item);
          if (item.children) item.children.forEach(findPaths);
        }
        findPaths(scope.project.activeLayer);

        allPaths.forEach((path) => {
          path.segments.forEach((seg) => {
            if (rect.contains(seg.point)) {
              seg.selected = true;
              selectedSegs.add(seg);
            }
          });
        });
      }

      if (selectionRect) {
        selectionRect.remove();
        selectionRect = null;
      }

      dragSegment = null;
      dragHandle = null;
      isDraggingSelection = false;
      dragStart = null;
    };

    nodeTool.onKeyDown = (e) => {
      if ((e.key === "delete" || e.key === "backspace") && selectedSegs.size > 0) {
        saveUndo();

        // 패스별로 그룹핑
        const pathMap = new Map();
        selectedSegs.forEach((seg) => {
          const path = seg.path;
          if (!path) return;
          if (!pathMap.has(path)) pathMap.set(path, []);
          pathMap.get(path).push(seg);
        });

        pathMap.forEach((segs, path) => {
          const remaining = path.segments.length - segs.length;
          if (remaining < 1) {
            // 포인트가 모두 삭제되면 패스 제거
            path.remove();
          } else {
            // 선택된 포인트만 제거 — 양쪽이 자동으로 이어짐
            segs.forEach((s) => s.remove());
          }
        });

        selectedSegs = new Set();
        updateHasContent();
      }
    };

    nodeTool.activate();
  }, []);

  // 펜 도구 (자유 곡선)
  const setupPenTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const penTool = new scope.Tool();
    let currentPath = null;

    penTool.onMouseDown = (e) => {
      saveUndo();
      currentPath = new scope.Path({
        strokeColor: new scope.Color(0, 0, 0),
        strokeWidth: strokeWidth,
        strokeCap: "round",
        strokeJoin: "round",
      });
      currentPath.add(e.point);
    };

    penTool.onMouseDrag = (e) => {
      if (currentPath) {
        currentPath.add(e.point);
      }
    };

    penTool.onMouseUp = () => {
      if (currentPath) {
        currentPath.simplify(10);
        currentPath = null;
      }
      updateHasContent();
    };

    penTool.activate();
  }, [strokeWidth]);

  // 직선 도구
  const setupLineTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const lineTool = new scope.Tool();
    let startPoint = null;
    let previewLine = null;

    lineTool.onMouseDown = (e) => {
      saveUndo();
      startPoint = e.point;
    };

    lineTool.onMouseDrag = (e) => {
      if (previewLine) previewLine.remove();
      previewLine = new scope.Path.Line({
        from: startPoint,
        to: e.point,
        strokeColor: new scope.Color(0, 0, 0),
        strokeWidth: strokeWidth,
        strokeCap: "round",
      });
    };

    lineTool.onMouseUp = () => {
      startPoint = null;
      previewLine = null;
      updateHasContent();
    };

    lineTool.activate();
  }, [strokeWidth]);

  // 원 도구
  const setupCircleTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const circleTool = new scope.Tool();
    let startPoint = null;
    let previewCircle = null;

    circleTool.onMouseDown = (e) => {
      saveUndo();
      startPoint = e.point;
    };

    circleTool.onMouseDrag = (e) => {
      if (previewCircle) previewCircle.remove();
      const radius = startPoint.getDistance(e.point);
      previewCircle = new scope.Path.Circle({
        center: startPoint,
        radius: radius,
        strokeColor: new scope.Color(0, 0, 0),
        strokeWidth: strokeWidth,
      });
    };

    circleTool.onMouseUp = () => {
      startPoint = null;
      previewCircle = null;
      updateHasContent();
    };

    circleTool.activate();
  }, [strokeWidth]);

  // 사각형 도구
  const setupRectTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const rectTool = new scope.Tool();
    let startPoint = null;
    let previewRect = null;

    rectTool.onMouseDown = (e) => {
      saveUndo();
      startPoint = e.point;
    };

    rectTool.onMouseDrag = (e) => {
      if (previewRect) previewRect.remove();
      previewRect = new scope.Path.Rectangle({
        from: startPoint,
        to: e.point,
        strokeColor: new scope.Color(0, 0, 0),
        strokeWidth: strokeWidth,
      });
    };

    rectTool.onMouseUp = () => {
      startPoint = null;
      previewRect = null;
      updateHasContent();
    };

    rectTool.activate();
  }, [strokeWidth]);

  // 지우개 도구 (자유 드로잉 — 지나간 부분만 잘라서 삭제)
  const setupEraserTool = useCallback(() => {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const eraserTool = new scope.Tool();
    let cursorCircle = null;
    let undoSaved = false;
    const eraserSize = strokeWidth * 2;

    function eraseAtPoint(point) {
      // 모든 레벨의 아이템을 재귀적으로 수집
      function collectAll(parent) {
        const result = [];
        if (!parent.children) return result;
        for (const child of [...parent.children]) {
          if (child.data?._eraser) continue;
          if (child.className === "CompoundPath" || child.className === "Path") {
            result.push(child);
          } else if (child.children) {
            result.push(...collectAll(child));
          }
        }
        return result;
      }
      const items = collectAll(scope.project.activeLayer);

      const eraserCircle = new scope.Path.Circle({
        center: point,
        radius: eraserSize,
        insert: false,
      });

      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];

        // bounds 체크로 빠른 필터
        if (!item.bounds.intersects(eraserCircle.bounds)) continue;

        // fill 기반 (CompoundPath 또는 filled Path)
        const isFilled = (item.className === "CompoundPath") ||
          (item.className === "Path" && item.fillColor && (!item.strokeColor || item.strokeWidth === 0));

        if (isFilled) {
          if (!undoSaved) { saveUndo(); undoSaved = true; }
          try {
            const circle = new scope.Path.Circle({ center: point, radius: eraserSize });
            circle.fillColor = new scope.Color(0, 0, 0);
            const result = item.subtract(circle);
            circle.remove();
            if (result && (result.area === undefined || Math.abs(result.area) > 1)) {
              result.fillColor = item.fillColor?.clone() || new scope.Color(0, 0, 0);
              result.fillRule = item.fillRule || "evenodd";
              result.strokeColor = null;
              item.replaceWith(result);
            } else {
              item.remove();
              if (result) result.remove();
            }
          } catch {
            item.remove();
          }
          continue;
        }

        // stroke 기반 path
        if (item.className !== "Path") continue;
        if (!item.segments || item.segments.length < 2) continue;

        const nearest = item.getNearestLocation(point);
        if (!nearest || nearest.distance > eraserSize) continue;

        if (!undoSaved) { saveUndo(); undoSaved = true; }

        const offset = nearest.offset;
        const len = item.length;
        const cutBefore = Math.max(0, offset - eraserSize);
        const cutAfter = Math.min(len, offset + eraserSize);

        if (cutBefore <= 0.5 && cutAfter >= len - 0.5) {
          item.remove();
          continue;
        }

        const style = {
          strokeColor: item.strokeColor?.clone(),
          strokeWidth: item.strokeWidth,
          strokeCap: item.strokeCap,
          strokeJoin: item.strokeJoin,
          fillColor: item.fillColor?.clone(),
        };

        if (cutBefore <= 0.5) {
          const afterPart = item.splitAt(cutAfter);
          item.remove();
          if (afterPart) {
            afterPart.strokeColor = style.strokeColor;
            afterPart.strokeWidth = style.strokeWidth;
          }
        } else if (cutAfter >= len - 0.5) {
          const tail = item.splitAt(cutBefore);
          if (tail) tail.remove();
        } else {
          const afterPart = item.splitAt(cutAfter);
          const middlePart = item.splitAt(cutBefore);
          if (middlePart) middlePart.remove();
          if (afterPart) {
            afterPart.strokeColor = style.strokeColor;
            afterPart.strokeWidth = style.strokeWidth;
          }
        }
      }
    }

    eraserTool.onMouseDown = (e) => {
      undoSaved = false;
      cursorCircle = new scope.Path.Circle({
        center: e.point,
        radius: eraserSize,
        strokeColor: new scope.Color(1, 0, 0, 0.4),
        strokeWidth: 1,
        dashArray: [3, 3],
      });
      cursorCircle.data._eraser = true;

      eraseAtPoint(e.point);
    };

    eraserTool.onMouseDrag = (e) => {
      if (cursorCircle) cursorCircle.position = e.point;
      eraseAtPoint(e.point);
    };

    eraserTool.onMouseUp = () => {
      if (cursorCircle) {
        cursorCircle.remove();
        cursorCircle = null;
      }
      updateHasContent();
    };

    eraserTool.activate();
  }, [strokeWidth]);

  useEffect(() => {
    if (!canvasReady) return;
    toolRef.current = tool;

    // 도구 전환 시 잔존하는 _eraser 커서 제거
    const scope = paperScopeRef.current;
    if (scope) {
      const layer = scope.project.activeLayer;
      for (let i = layer.children.length - 1; i >= 0; i--) {
        if (layer.children[i].data?._eraser) layer.children[i].remove();
      }
    }

    if (tool === "select") setupSelectTool();
    else if (tool === "node") setupNodeTool();
    else if (tool === "pen") setupPenTool();
    else if (tool === "line") setupLineTool();
    else if (tool === "circle") setupCircleTool();
    else if (tool === "rect") setupRectTool();
    else if (tool === "eraser") setupEraserTool();
  }, [tool, canvasReady, strokeWidth, setupSelectTool, setupNodeTool, setupPenTool, setupLineTool, setupCircleTool, setupRectTool, setupEraserTool]);

  function saveUndo() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    undoStack.current.push(scope.project.exportJSON());
    redoStack.current = [];
  }

  // 모든 Path를 재귀적으로 찾기
  function getAllPaths(item) {
    const paths = [];
    if (item.className === "Path") {
      paths.push(item);
    } else if (item.children) {
      item.children.forEach((child) => paths.push(...getAllPaths(child)));
    }
    return paths;
  }

  // 전체 선택
  function selectAll() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    const paths = getAllPaths(scope.project.activeLayer);
    paths.forEach((p) => { p.fullySelected = true; });
  }

  /* ===== 이미 그려진 그림의 선 굵기 조절 =====
     AI 디자인은 획이 아니라 검정 fill 도형(CompoundPath)으로 들어오므로
     "굵기"는 곧 도형의 두께다. fill과 같은 색 외곽선을 얹으면 경계가 delta/2 만큼
     바깥으로, 흰색이면 안쪽으로 밀린다. 구멍(evenodd) 경계는 반대로 움직이므로
     팽창/침식이 그대로 맞아떨어진다.
     커터·SVG로 넘길 때 캔버스를 PNG로 구워 potrace로 다시 따고(goToCutter),
     서버가 흰색을 배경으로 지우므로(main.py: -background white) 화면에 보이는
     굵기가 그대로 커터 형상이 된다. */

  // 굵기 조절 대상 수집 (CompoundPath는 통째로, 내부 자식은 건드리지 않는다)
  function getThicknessTargets(item) {
    const result = [];
    if (item.className === "CompoundPath") {
      result.push(item);
    } else if (item.className === "Path" && item.segments?.length >= 2) {
      result.push(item);
    } else if (item.children) {
      item.children.forEach((child) => result.push(...getThicknessTargets(child)));
    }
    return result;
  }

  // 항목별 원래 획 정보 — 슬라이더를 움직여도 조절이 누적되지 않도록 기준값을 보관
  function getThicknessBase(item) {
    if (!item.data) item.data = {};
    if (!item.data.thickBase) {
      const filled = !!item.fillColor && (!item.strokeColor || item.strokeWidth === 0);
      item.data.thickBase = {
        mode: filled ? "fill" : "stroke",
        width: filled ? 0 : item.strokeWidth || 0,
        color: item.strokeColor ? item.strokeColor.toCSS(true) : null,
      };
    }
    return item.data.thickBase;
  }

  function applyThickness(delta) {
    const scope = paperScopeRef.current;
    if (!scope) return;

    const all = getThicknessTargets(scope.project.activeLayer).filter(
      (it) => !it.data?._eraser && (it.fillColor || it.strokeColor)
    );
    if (all.length === 0) return;

    // 선택한 항목이 있으면 그것만, 없으면 전체
    const selected = all.filter((it) => it.selected || it.fullySelected);
    const targets = selected.length > 0 ? selected : all;

    targets.forEach((item) => {
      const base = getThicknessBase(item);
      if (base.mode === "fill") {
        if (delta === 0) {
          item.strokeColor = base.color ? new scope.Color(base.color) : null;
          item.strokeWidth = base.width;
        } else {
          // 굵게: fill과 같은 색 / 얇게: 배경색(흰색)
          item.strokeColor = delta > 0 ? item.fillColor.clone() : new scope.Color(1, 1, 1);
          item.strokeWidth = Math.abs(delta);
          item.strokeCap = "round";
          /* miter로 바꿔 각을 살리는 걸 시도했으나 효과가 없었다.
             potrace가 만든 외곽선은 코너가 이미 매끄러운 곡선이라
             miter가 걸릴 꼭짓점 자체가 없다. round가 안전하다. */
          item.strokeJoin = "round";
        }
      } else {
        // 손으로 그린 획 기반 path는 굵기를 직접 바꾼다
        item.strokeWidth = Math.max(MIN_STROKE_WIDTH, base.width + delta);
      }
    });
  }

  // 슬라이더 조작 1회당 undo 1번만 쌓는다
  function beginThicknessEdit() {
    if (thicknessEditingRef.current) return;
    thicknessEditingRef.current = true;
    saveUndo();
  }

  function endThicknessEdit() {
    if (!thicknessEditingRef.current) return;
    thicknessEditingRef.current = false;
    updateHasContent(); // 초안 저장
  }

  function resetThickness() {
    saveUndo();
    applyThickness(0);
    setThicknessDelta(0);
    updateHasContent();
  }

  // 선택된 앵커포인트 2개를 합치기 (노드 도구용)
  function joinSelectedAnchors() {
    const scope = paperScopeRef.current;
    if (!scope) return;

    // 모든 패스에서 selected 세그먼트 수집
    const allPaths = getAllPaths(scope.project.activeLayer);
    const selectedSegs = [];
    allPaths.forEach((path) => {
      path.segments.forEach((seg) => {
        if (seg.selected) selectedSegs.push(seg);
      });
    });

    if (selectedSegs.length !== 2) {
      setError(t("design.joinSelectTwoAnchors"));
      setTimeout(() => setError(""), 3000);
      return;
    }

    const segA = selectedSegs[0];
    const segB = selectedSegs[1];
    const midPoint = segA.point.add(segB.point).divide(2);

    saveUndo();

    if (segA.path === segB.path) {
      // 같은 패스 → 중간점으로 합침
      segA.point = midPoint;
      segB.remove();
    } else {
      // 다른 패스 → 선택한 포인트 기준으로 합침
      const pathA = segA.path;
      const pathB = segB.path;
      const idxA = segA.index;
      const idxB = segB.index;

      // pathA에서 segA 이후 세그먼트들 + pathB에서 segB 이후 세그먼트들을 연결
      // segA를 midPoint로 이동, segA 뒤의 세그먼트는 유지
      // pathB를 segB 기준으로 분할하여 pathA에 연결

      // pathA: segA 이후를 잘라냄
      const afterA = [];
      for (let i = idxA + 1; i < pathA.segments.length; i++) {
        const s = pathA.segments[i];
        afterA.push(new scope.Segment(s.point.clone(), s.handleIn.clone(), s.handleOut.clone()));
      }
      // segA 이후 세그먼트 제거
      while (pathA.segments.length > idxA + 1) {
        pathA.segments[pathA.segments.length - 1].remove();
      }
      pathA.lastSegment.point = midPoint;

      // pathB: segB 이후 세그먼트를 pathA에 추가
      for (let i = idxB + 1; i < pathB.segments.length; i++) {
        const s = pathB.segments[i];
        pathA.add(new scope.Segment(s.point.clone(), s.handleIn.clone(), s.handleOut.clone()));
      }

      // pathB: segB 이전 세그먼트가 남아있으면 별도 패스로 유지
      if (idxB > 0) {
        const remainPath = new scope.Path({
          strokeColor: pathB.strokeColor?.clone() || pathA.strokeColor?.clone(),
          strokeWidth: pathB.strokeWidth || pathA.strokeWidth,
          strokeCap: pathB.strokeCap,
          strokeJoin: pathB.strokeJoin,
        });
        for (let i = 0; i < idxB; i++) {
          const s = pathB.segments[i];
          remainPath.add(new scope.Segment(s.point.clone(), s.handleIn.clone(), s.handleOut.clone()));
        }
      }

      // afterA가 있으면 별도 패스로 유지
      if (afterA.length > 0) {
        const tailPath = new scope.Path({
          strokeColor: pathA.strokeColor?.clone(),
          strokeWidth: pathA.strokeWidth,
          strokeCap: pathA.strokeCap,
          strokeJoin: pathA.strokeJoin,
        });
        afterA.forEach((s) => tailPath.add(s));
      }

      pathA.strokeColor = pathA.strokeColor || pathB.strokeColor;
      pathA.strokeWidth = pathA.strokeWidth || pathB.strokeWidth;
      pathB.remove();
    }

    scope.project.deselectAll();
    updateHasContent();
  }

  // 확대/축소 버튼
  function getContentCenter() {
    const scope = paperScopeRef.current;
    if (!scope) return null;
    const bounds = scope.project.activeLayer.bounds;
    if (bounds.width === 0 && bounds.height === 0) return scope.view.center;
    return bounds.center;
  }

  function zoomIn() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    scope.view.scale(1.2, getContentCenter() || scope.view.center);
  }

  function zoomOut() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    scope.view.scale(0.8, getContentCenter() || scope.view.center);
  }

  function zoomReset() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    const bounds = scope.project.activeLayer.bounds;
    if (bounds.width > 0 && bounds.height > 0) {
      // 콘텐츠에 맞춰서 화면 맞춤
      const padding = 0.85;
      const zoom = Math.min(
        (scope.view.viewSize.width * padding) / bounds.width,
        (scope.view.viewSize.height * padding) / bounds.height
      );
      scope.view.zoom = zoom;
      scope.view.center = bounds.center;
    } else {
      scope.view.zoom = 1;
      scope.view.center = new scope.Point(
        scope.view.viewSize.width / 2,
        scope.view.viewSize.height / 2
      );
    }
  }

  function undo() {
    const scope = paperScopeRef.current;
    if (!scope || !undoStack.current.length) return;
    redoStack.current.push(scope.project.exportJSON());
    const state = undoStack.current.pop();
    scope.project.clear();
    scope.project.importJSON(state);
  }

  function redo() {
    const scope = paperScopeRef.current;
    if (!scope || !redoStack.current.length) return;
    undoStack.current.push(scope.project.exportJSON());
    const state = redoStack.current.pop();
    scope.project.clear();
    scope.project.importJSON(state);
  }

  function clearCanvas() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    saveUndo();
    scope.project.clear();
    setThicknessDelta(0);
    updateHasContent();
  }

  // AI 이미지 생성
  async function handleGenerate() {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    setGenerating(true);
    setError("");
    setImageUrl(null);

    /* 같은 프롬프트로 다시 생성하면 서버가 변주 지시문을 붙이도록 시도 횟수를 보낸다.
       프롬프트가 같으면 요청도 완전히 같아서 거의 똑같은 그림이 나오기 때문이다.
       실패한 시도는 세지 않도록 성공했을 때만 올린다. */
    const attempt = attemptRef.current.prompt === trimmed ? attemptRef.current.count + 1 : 0;

    const controller = new AbortController();
    generateAbortRef.current = controller;

    try {
      const res = await fetch(`/ai/generate-design`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: trimmed, user_id: user?.id || "", attempt }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 429) {
          throw new Error(t("design.aiLimitReached"));
        }
        if (err.detail === "CONTENT_POLICY") {
          throw new Error(t("design.contentPolicyError"));
        }
        throw new Error(err.detail || t("design.generateFailed"));
      }

      const data = await res.json();
      const pngDataUrl = `data:image/png;base64,${data.image_base64}`;
      attemptRef.current = { prompt: trimmed, count: attempt };
      setImageUrl(pngDataUrl);
      trackAdActivity("design_generated");
      setHistory((prev) => [{ prompt: trimmed, url: pngDataUrl }, ...prev].slice(0, 10));

      // 즉시 카운트 +1 (UI 딜레이 방지 - fetchUsage는 finally에서 보정)
      setAiUsage((prev) => (prev ? { ...prev, used: (prev.used || 0) + 1 } : prev));

      // 자동 SVG 변환
      await convertToSvgFromBase64(data.image_base64);

      // 자동 저장 — 저장 버튼을 누른 게 아니므로 임시저장으로 들어간다
      if (user?.id) {
        const autoName = trimmed.slice(0, 30) || t("design.untitled");
        await saveDesign(autoName, { draft: true });
      }
    } catch (err) {
      // 사용자가 직접 취소한 건 오류가 아니다
      if (err.name !== "AbortError") setError(err.message);
    } finally {
      generateAbortRef.current = null;
      setGenerating(false);
      fetchUsage(); // 취소해도 서버는 이미 생성 중이라 실제 사용량을 다시 읽어온다
    }
  }

  /* 생성 취소 — 기다리는 것만 멈춘다.
     이미 이미지 생성 요청이 나간 뒤라 일일 사용 횟수는 그대로 차감된다.
     (OpenAI 쪽 생성을 중간에 되돌릴 방법이 없다) */
  function cancelGenerate() {
    generateAbortRef.current?.abort();
  }

  // base64 PNG → SVG 변환 & Paper.js에 로드
  async function convertToSvgFromBase64(base64) {
    setConverting(true);
    try {
      const byteString = atob(base64);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: "image/png" });

      const formData = new FormData();
      formData.append("file", new File([blob], "design.png", { type: "image/png" }));
      if (user?.id) formData.append("user_id", user.id);
      /* 중심선 모드(centerline=1)는 검증이 끝날 때까지 끈다.
         분기점 토막이 검은 점으로 찍히는 문제가 남아 있다. */

      const svgRes = await fetch(`${API_BASE}/png_to_svg`, {
        method: "POST",
        body: formData,
      });

      if (svgRes.status === 429) {
        const err = await svgRes.json().catch(() => ({}));
        throw new Error(err.detail || "AI 디자인 일일 사용 한도에 도달했습니다.");
      }
      if (!svgRes.ok) throw new Error("SVG 변환 실패");

      const svgText = await svgRes.text();
      await loadSvgToCanvas(svgText);
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  }

  // SVG import 후 흰색 path 제거 → CompoundPath + evenodd로 투명 구멍 처리
  function flattenSvgPaths(rootItem) {
    const scope = paperScopeRef.current;
    if (!scope) return;

    // 모든 항목을 재귀적으로 수집 (CompoundPath, Path)
    function collectItems(item) {
      const result = [];
      if (item.className === "CompoundPath") {
        result.push(item);
      } else if (item.className === "Path" && item.segments && item.segments.length >= 2) {
        result.push(item);
      } else if (item.children) {
        item.children.forEach(child => result.push(...collectItems(child)));
      }
      return result;
    }

    const items = collectItems(rootItem);
    if (items.length === 0) return;

    // 흰색 fill을 가진 독립 Path만 제거, CompoundPath는 evenodd 적용
    for (const item of items) {
      /* 중심선 모드로 받은 획(fill 없이 stroke만 있는 path)은 그대로 살린다.
         여기서 fill을 씌우면 손으로 그린 선과 같은 형태가 깨진다. */
      if (item.strokeColor && !item.fillColor) {
        item.strokeColor = new scope.Color(0, 0, 0);
        continue;
      }
      if (item.className === "CompoundPath") {
        item.fillRule = "evenodd";
        item.fillColor = new scope.Color(0, 0, 0);
        item.strokeColor = null;
      } else if (item.className === "Path") {
        const fc = item.fillColor;
        if (fc) {
          const gray = fc.gray !== undefined ? fc.gray : (fc.red + fc.green + fc.blue) / 3;
          if (gray > 0.9) {
            item.remove(); // 흰색 path 제거
            continue;
          }
        }
        item.fillColor = new scope.Color(0, 0, 0);
        item.strokeColor = null;
      }
    }
  }

  function loadSvgToCanvas(svgText) {
    const scope = paperScopeRef.current;
    if (!scope) return Promise.resolve();

    scope.project.clear();
    undoStack.current = [];
    redoStack.current = [];
    setThicknessDelta(0); // 새 그림이므로 굵기 보정도 원점으로

    // viewSize 백업
    const origSize = scope.view.viewSize.clone();

    return new Promise((resolve) => {
      scope.project.importSVG(svgText, {
        onLoad: (item) => {
        // importSVG가 변경한 viewSize 복원
        scope.view.viewSize = origSize;

        // 흰색 path 제거 처리
        flattenSvgPaths(item);

        // Group/클립 껍데기를 벗겨서 path를 activeLayer 직속으로 이동
        const layer = scope.project.activeLayer;
        function unwrapGroups() {
          let found = true;
          while (found) {
            found = false;
            [...layer.children].forEach(child => {
              if (child.className === "Group" || child.className === "Layer") {
                const inner = [...child.children];
                inner.forEach(c => layer.addChild(c));
                child.remove();
                found = true;
              }
            });
          }
          // 클립 마스크 역할의 사각형 제거
          [...layer.children].forEach(child => {
            // 획(stroke)만 있는 path는 클립 마스크가 아니라 그림이므로 지우면 안 된다
            if (child.className === "Shape" ||
                (child.className === "Path" && child.segments?.length === 4 &&
                 !child.fillColor && !child.strokeColor)) {
              child.remove();
            }
          });
        }
        unwrapGroups();

        // 스케일 및 가운데 정렬
        const paths = [...layer.children];
        if (paths.length > 0) {
          let bounds = paths[0].bounds.clone();
          paths.forEach(p => { bounds = bounds.unite(p.bounds); });

          const viewSize = scope.view.viewSize;
          const scale = Math.min(
            (viewSize.width * 0.8) / bounds.width,
            (viewSize.height * 0.8) / bounds.height
          );

          // 스케일 적용 후 가운데 이동
          // paper.js는 변환할 때 획 굵기를 함께 줄이지 않으므로 직접 맞춰준다.
          // (안 하면 중심선 모드로 받은 획이 1/scale 배로 굵게 보인다)
          paths.forEach(p => {
            p.scale(scale, bounds.center);
            if (p.strokeWidth) p.strokeWidth *= scale;
          });
          // bounds 재계산
          let newBounds = paths[0].bounds.clone();
          paths.forEach(p => { newBounds = newBounds.unite(p.bounds); });
          const move = scope.view.center.subtract(newBounds.center);
          paths.forEach(p => p.translate(move));
        }

        saveUndo();
        updateHasContent();
        resolve();
        },
      });
    });
  }

  // 캔버스에 내용이 있는지 체크 → 상태 갱신
  function updateHasContent() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    setHasContent(scope.project.activeLayer.children.length > 0);
    saveDraft();
  }

  /* ===== 작업 중이던 내용 보존 =====
     저장 버튼을 안 눌러도 다른 페이지에 다녀오면 이어서 작업할 수 있게
     캔버스를 세션에 남긴다. 탭을 닫으면 사라지므로 "저장한 디자인"과 헷갈리지 않는다.
     저장/불러오기와 같은 exportJSON/importJSON을 쓴다. */
  function writeDraftNow() {
    const scope = paperScopeRef.current;
    // 복원 전에 기록하면 빈 캔버스로 초안을 덮어쓴다
    if (!scope || !draftRestoredRef.current) return;
    try {
      if (!scope.project?.activeLayer?.children.length) {
        sessionStorage.removeItem(DRAFT_KEY);
        return;
      }
      const canvasData = scope.project.exportJSON();
      if (!canvasData || canvasData.length > DRAFT_MAX_CHARS) {
        console.info("[디자인] 작업 내용이 너무 커서 임시 보관하지 않음", canvasData?.length);
        sessionStorage.removeItem(DRAFT_KEY);
        return;
      }
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ canvasData, savedAt: Date.now(), ...draftMetaRef.current })
      );
    } catch {
      // 용량 초과 등 — 초안 보존 실패가 작업 자체를 막으면 안 된다
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
    }
  }

  // 캔버스가 바뀔 때마다 호출된다. 잦은 편집에 매번 직렬화하지 않도록 묶어서 기록.
  function saveDraft() {
    if (!draftRestoredRef.current) return;
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(writeDraftNow, 500);
  }

  function restoreDraft() {
    draftRestoredRef.current = true;
    const scope = paperScopeRef.current;
    if (!scope) return;

    let draft = null;
    try {
      draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    } catch {}
    if (!draft?.canvasData) return;
    if (!draft.savedAt || Date.now() - draft.savedAt > DRAFT_TTL_MS) {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
      return;
    }

    // loadDesign과 같은 순서로 복원
    scope.project.clear();
    scope.project.activeLayer.removeChildren();
    undoStack.current = [];
    redoStack.current = [];
    setThicknessDelta(0);
    try {
      scope.project.importJSON(draft.canvasData);
      scope.view.zoom = 1;
      const bounds = scope.project.activeLayer.bounds;
      if (bounds.width > 0 && bounds.height > 0) scope.view.center = bounds.center;
    } catch {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
      return;
    }
    if (draft.prompt) setPrompt(draft.prompt);
    if (draft.activeDesignId) setActiveDesignId(draft.activeDesignId);
    updateHasContent();
  }

  // 썸네일 생성
  function generateThumbnail() {
    const scope = paperScopeRef.current;
    if (!scope) return null;
    try {
      const svg = scope.project.exportSVG({ asString: true });
      const encoded = btoa(unescape(encodeURIComponent(svg)));
      return `data:image/svg+xml;base64,${encoded}`;
    } catch {
      return null;
    }
  }

  // 저장 버튼 클릭 → 이름 입력 모달 열기
  function openSaveModal() {
    // 기존 디자인이면 현재 이름 가져오기
    const current = savedDesigns.find((d) => d.id === activeDesignId);
    const defaultName = current?.name || prompt.trim() || t("design.untitled");
    setSaveModal({ open: true, name: defaultName });
  }

  /* 실제 저장 (이름 확정 후 호출)
     draft=true는 생성 직후 자동저장 — 저장 버튼을 누른 게 아니므로 임시저장으로 분류한다.
     저장 버튼을 누르면 정식 저장으로 승격시키되, 자동저장이 등급을 되돌리지는 않는다. */
  async function saveDesign(saveName, { draft = false } = {}) {
    const scope = paperScopeRef.current;
    if (!scope || !user?.id) return;
    setSaving(true);
    setSaveMsg("");
    try {
      // 정식 저장은 10개까지 (휴지통·임시저장 제외). 이미 정식 저장된 디자인을 덮어쓰는 건 허용.
      if (!draft && user.role !== "admin" && !savedDesigns.some((d) => d.id === activeDesignId)) {
        const { count, error: countErr } = await supabase
          .from("designs")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .not("is_deleted", "is", true)
          .not("is_draft", "is", true);
        if (countErr) throw countErr;
        if ((count || 0) >= DESIGN_SAVE_LIMIT) {
          showAlert(t("design.saveLimitReached", { limit: DESIGN_SAVE_LIMIT }));
          return;
        }
      }

      const canvasData = scope.project.exportJSON();
      const thumbnail = generateThumbnail();
      const name = saveName.trim() || t("design.untitled");

      if (activeDesignId) {
        // 기존 디자인 업데이트 (이름 + 데이터)
        const patch = { name, canvas_data: canvasData, thumbnail, updated_at: new Date().toISOString() };
        if (!draft) patch.is_draft = false;
        const { error } = await supabase.from("designs")
          .update(patch)
          .eq("id", activeDesignId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("designs")
          .insert({ user_id: user.id, name, canvas_data: canvasData, thumbnail, is_draft: draft })
          .select("id")
          .single();
        if (error) throw error;
        setActiveDesignId(data.id);
      }
      setSaveMsg(t(draft ? "design.draftSaved" : "design.saved"));
      fetchSavedDesigns();
    } catch {
      setSaveMsg(t("design.saveFailed"));
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 2000);
    }
  }

  // 이름 변경
  async function renameDesign(designId, newName) {
    if (!newName.trim()) return;
    await supabase.from("designs")
      .update({ name: newName.trim() })
      .eq("id", designId);
    fetchSavedDesigns();
  }

  // 전체선택은 정식 저장 + 임시저장을 함께 다룬다
  const allDesigns = [...savedDesigns, ...draftDesigns];

  // 목록 항목 — 정식 저장과 임시저장이 같은 모양을 쓴다
  function renderDesignItem(d) {
    return (
      <div
        key={d.id}
        className={`design-saved-item ${activeDesignId === d.id ? "active" : ""}`}
        onClick={() => loadDesign(d.id)}
      >
        <input
          type="checkbox"
          className="design-saved-checkbox"
          checked={selectedDesignIds.includes(d.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedDesignIds((prev) => [...prev, d.id]);
            } else {
              setSelectedDesignIds((prev) => prev.filter((id) => id !== d.id));
            }
          }}
        />
        <div className="design-saved-thumb">
          {d.thumbnail ? (
            <img src={d.thumbnail} alt="" />
          ) : (
            <div className="design-saved-thumb-empty" />
          )}
        </div>
        <div className="design-saved-info">
          <span
            className="design-saved-name editable"
            onClick={(e) => {
              e.stopPropagation();
              setRenameModal({ open: true, id: d.id, name: d.name });
            }}
            title={t("design.renameHint")}
          >
            {d.name}
            <svg className="design-saved-edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </span>
          <span className="design-saved-date">
            {new Date(d.updated_at).toLocaleDateString()}
          </span>
        </div>
        <button
          className="design-saved-delete"
          onClick={async (e) => {
            e.stopPropagation();
            if (await showConfirm(t("design.deleteConfirm"))) deleteDesign(d.id);
          }}
          title={t("design.delete")}
        >
          ×
        </button>
      </div>
    );
  }

  // 저장된 디자인 불러오기
  async function loadDesign(designId) {
    const scope = paperScopeRef.current;
    if (!scope) return;
    const { data } = await supabase
      .from("designs")
      .select("canvas_data")
      .eq("id", designId)
      .single();

    // 캔버스 완전 초기화
    scope.project.clear();
    scope.project.activeLayer.removeChildren();
    undoStack.current = [];
    redoStack.current = [];
    setThicknessDelta(0);

    if (data?.canvas_data) {
      try {
        scope.project.importJSON(data.canvas_data);
        // 뷰 리셋 후 콘텐츠를 가운데로
        scope.view.zoom = 1;
        const bounds = scope.project.activeLayer.bounds;
        if (bounds.width > 0 && bounds.height > 0) {
          scope.view.center = bounds.center;
        }
      } catch {}
    }
    setActiveDesignId(designId);
    updateHasContent();
  }

  // URL 쿼리 파라미터로 디자인 자동 로드, 없으면 작업 중이던 초안 복원
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!canvasReady || initialLoadDone.current) return;
    const params = new URLSearchParams(location.search);
    const designId = params.get("id");
    if (designId) {
      initialLoadDone.current = true;
      draftRestoredRef.current = true; // 명시적으로 연 디자인이 우선
      loadDesign(designId);
      return;
    }
    if (!draftRestoredRef.current) restoreDraft();
  }, [canvasReady, location.search]);

  // 저장된 디자인 삭제
  async function deleteDesign(designId) {
    await supabase.from("designs").update({ is_deleted: true, deleted_at: new Date().toISOString() }).eq("id", designId);
    if (activeDesignId === designId) setActiveDesignId(null);
    fetchSavedDesigns();
  }

  // 새 디자인 (캔버스 초기화)
  function newDesign() {
    const scope = paperScopeRef.current;
    if (!scope) return;
    saveUndo();
    scope.project.clear();
    setActiveDesignId(null);
    setThicknessDelta(0);
    updateHasContent();
  }

  // 현재 디자인 파일명
  function getDesignName() {
    const current = savedDesigns.find((d) => d.id === activeDesignId);
    return current?.name || prompt.trim() || "design";
  }

  // 다운로드
  function downloadPng() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${getDesignName()}.png`;
    a.click();
  }

  async function downloadSvg() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setConverting(true);
    setError("");
    try {
      // 캔버스 → PNG blob
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const formData = new FormData();
      formData.append("file", new File([blob], "design.png", { type: "image/png" }));
      if (user?.id) formData.append("user_id", user.id);

      const res = await fetch(`${API_BASE}/png_to_svg`, {
        method: "POST",
        body: formData,
      });

      if (res.status === 429) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t("design.aiDesignLimitReached"));
      }
      if (!res.ok) throw new Error(t("design.svgConvertFailed"));

      const svgText = await res.text();
      const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(svgBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${getDesignName()}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  }

  async function goToCutter() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setConverting(true);
    setError("");
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const formData = new FormData();
      formData.append("file", new File([blob], "design.png", { type: "image/png" }));
      if (user?.id) formData.append("user_id", user.id);

      const res = await fetch(`${API_BASE}/png_to_svg`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(t("design.svgConvertFailed"));

      const svgText = await res.text();
      sessionStorage.setItem("designSvg", svgText);
      window.location.href = "/cutter?fromDesign=true";
    } catch (err) {
      setError(err.message);
    } finally {
      setConverting(false);
    }
  }

  const isDrawTool = tool === "pen" || tool === "line" || tool === "circle" || tool === "rect" || tool === "eraser";

  // 리뉴얼(2026-10-15): AI로 그리기 / 직접 그리기 전환. 직접 그리기로 가면 펜을 바로 쥐여 준다
  function switchDrawMode(m) {
    setDrawMode(m);
    if (m === "hand" && !isDrawTool) setTool("pen");
    if (m === "ai" && isDrawTool) setTool("select");
  }

  return (
    <div className="design-page ckd">
      {/* 좌측 패널 — 2026-10-15 리뉴얼: [AI로 그리기]/[직접 그리기] 전환 + 아래 버튼 고정 */}
      <div className="design-sidebar ckd-panel">
        <div className="ckd-scroll">
          <h2 className="ckd-title">{t("nav.design")}</h2>
          <div className="ckd-sub">{t("designUi.subtitle")}</div>

          <div className="ckd-modes">
            <button type="button" className={drawMode === "ai" ? "on" : ""} onClick={() => switchDrawMode("ai")}>✨ {t("designUi.modeAi")}</button>
            <button type="button" className={drawMode === "hand" ? "on" : ""} onClick={() => switchDrawMode("hand")}>✏️ {t("designUi.modeHand")}</button>
          </div>

          {drawMode === "ai" ? (
            <>
              <section className="ckd-sec">
                <div className="ckd-sh"><span className="ckd-n">1</span><b>{t("designUi.describe")}</b></div>
                <textarea
                  ref={promptRef}
                  className="design-textarea ckd-ta"
                  placeholder={t("design.placeholder")}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onBeforeInput={(e) => {
                    if (e.inputType === "historyUndo" || e.inputType === "historyRedo") {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.shiftKey) redo();
                      else undo();
                    }
                  }}
                  disabled={generating}
                />
                <div className="ckd-chips">
                  <small>{t("designUi.tryThese")}</small>
                  {["ex1", "ex2", "ex3"].map((k) => (
                    <button type="button" key={k} onClick={() => setPrompt(t(`designUi.${k}`))} disabled={generating}>{t(`designUi.${k}`)}</button>
                  ))}
                </div>
                <div className="ckd-gen">
                  <button
                    type="button"
                    className="ckd-btn dark"
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim() || (aiUsage && aiUsage.limit > 0 && aiUsage.used >= aiUsage.limit)}
                  >
                    {generating ? t("design.generating") : t("designUi.draw")}
                  </button>
                  {generating && (
                    <button type="button" className="ckd-btn line" onClick={cancelGenerate}>{t("common.cancel")}</button>
                  )}
                  {hasContent && !generating && (
                    <button
                      type="button"
                      className="ckd-btn line"
                      onClick={handleGenerate}
                      disabled={!prompt.trim() || (aiUsage && aiUsage.limit > 0 && aiUsage.used >= aiUsage.limit)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8 M21 3v5h-5" /></svg>
                      {t("designUi.redraw")}
                    </button>
                  )}
                </div>
                {aiUsage && (
                  <div className="ckd-usage">
                    <span>
                      {aiUsage.limit === -1
                        ? t("design.adminUnlimited")
                        : t("design.usageInfo", { remaining: Math.max(0, aiUsage.limit - aiUsage.used) })}
                    </span>
                    {aiUsage.limit > 0 && (
                      <>
                        <span className="ckd-ubar"><i style={{ width: `${Math.min(100, (aiUsage.used / aiUsage.limit) * 100)}%` }} /></span>
                        <span>{aiUsage.used}/{aiUsage.limit}</span>
                      </>
                    )}
                  </div>
                )}
                <div className="ckd-tip">
                  <b>TIP</b> {t("designUi.tip")}<br />
                  <span className="no">✕ {t("designUi.tipBad")}</span> → <span className="ok">✓ {t("designUi.tipGood")}</span>
                </div>
                <details className="ckd-details">
                  <summary>{t("designUi.goodCutterTitle")}</summary>
                  <p>{t("guide.cutter.tip3")}</p>
                  <ClosedVsBrokenDiagram t={t} />
                </details>
                {error && <div className="design-error">{error}</div>}
              </section>

              {history.length > 0 && (
                <section className="ckd-sec">
                  <div className="ckd-sh"><span className="ckd-n">2</span><b>{t("designUi.pick")}</b><span className="ckd-hint">{t("designUi.pickHint")}</span></div>
                  <div className="ckd-hist">
                    {history.map((h, i) => (
                      <button
                        type="button"
                        key={i}
                        className={imageUrl === h.url ? "on" : ""}
                        title={h.prompt}
                        onClick={() => {
                          setImageUrl(h.url);
                          const b64 = h.url.replace("data:image/png;base64,", "");
                          convertToSvgFromBase64(b64);
                        }}
                      >
                        <img src={h.url} alt={h.prompt} />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <section className="ckd-sec">
              <div className="ckd-sh"><span className="ckd-n">1</span><b>{t("designUi.handTitle")}</b><span className="ckd-hint">{t("designUi.handFree")}</span></div>
              <div className="ckd-tools2">
                {[
                  ["pen", "toolPen", "handPen", <path key="p" d="M3 21c3-1 5-2 6-4l9-9-2-2-9 9c-2 1-3 3-4 6z M14 6l4 4" />],
                  ["line", "toolLine", "handLine", <path key="l" d="M5 19L19 5" />],
                  ["circle", "toolCircle", "handCircle", <circle key="c" cx="12" cy="12" r="8" />],
                  ["rect", "toolRect", "handRect", <rect key="r" x="4" y="5" width="16" height="14" rx="2" />],
                ].map(([k, nameKey, descKey, icon]) => (
                  <button type="button" key={k} className={tool === k ? "on" : ""} onClick={() => setTool(k)}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">{icon}</svg>
                    <b>{t(`design.${nameKey}`)}</b>
                    <small>{t(`designUi.${descKey}`)}</small>
                  </button>
                ))}
              </div>
              <div className="ckd-tip">
                <b>{t("designUi.handTipTitle")}</b><br />
                · {t("designUi.handTip1")}<br />
                · {t("designUi.handTip2")}<br />
                · {t("designUi.handTip3")}
              </div>
            </section>
          )}

          {/* 저장된 디자인 (로그인 유저만) */}
          {user && (
            <section className="ckd-sec design-saved">
              <div className="ckd-sh">
                <span className="ckd-n">{drawMode === "ai" ? (history.length > 0 ? 3 : 2) : 2}</span>
                <b>{t("design.savedDesigns")}</b>
                <span className="ckd-hint">
                  {t("designUi.savedCount", { count: savedDesigns.length, limit: DESIGN_SAVE_LIMIT, days: STORAGE_RETENTION_DAYS })}
                </span>
              </div>
              <div className="ckd-saved-acts">
                <button type="button" onClick={newDesign} title={t("design.newDesignHint")}>+ {t("design.newDesign")}</button>
                {allDesigns.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedDesignIds.length === allDesigns.length) setSelectedDesignIds([]);
                      else setSelectedDesignIds(allDesigns.map((d) => d.id));
                    }}
                  >
                    {selectedDesignIds.length === allDesigns.length ? t("design.deselectAll") : t("design.selectAll")}
                  </button>
                )}
              </div>
              {selectedDesignIds.length > 0 && (
                <div className="design-bulk-bar">
                  <span>{selectedDesignIds.length}{t("design.selectedCount")}</span>
                  <button
                    className="design-bulk-delete-btn"
                    onClick={async () => {
                      if (!(await showConfirm(t("design.bulkDeleteConfirm", { count: selectedDesignIds.length })))) return;
                      for (const id of selectedDesignIds) {
                        await deleteDesign(id);
                      }
                      setSelectedDesignIds([]);
                    }}
                  >
                    {t("design.bulkDelete")}
                  </button>
                </div>
              )}
              {savedDesigns.length === 0 ? (
                <div className="design-saved-empty">{t("design.noSaved")}</div>
              ) : (
                savedDesigns.map(renderDesignItem)
              )}

              {draftDesigns.length > 0 && (
                <div className="design-draft-section">
                  <div className="design-draft-header">
                    <h4>{t("design.draftsTitle")}</h4>
                    <span className="design-draft-notice">
                      {t("design.draftsNotice", { days: DRAFT_RETENTION_DAYS })}
                    </span>
                  </div>
                  {draftDesigns.map(renderDesignItem)}
                </div>
              )}
            </section>
          )}
        </div>

        {/* 아래 고정 버튼 */}
        <div className="ckd-actions">
          <button type="button" className="ckd-btn caramel" onClick={goToCutter} disabled={!hasContent}>{t("designUi.toCutter")} →</button>
          <div className={`ckd-mini ${user ? "" : "two"}`}>
            {user && (
              <button
                type="button"
                onClick={activeDesignId ? () => saveDesign(savedDesigns.find((d) => d.id === activeDesignId)?.name || t("design.untitled")) : openSaveModal}
                disabled={!hasContent || saving}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8" /></svg>
                {saving ? t("design.saving") : t("design.save")}
              </button>
            )}
            <button type="button" onClick={downloadPng} disabled={!hasContent}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11 M7 10l5 5 5-5 M5 20h14" /></svg>PNG
            </button>
            <button type="button" onClick={downloadSvg} disabled={!hasContent}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11 M7 10l5 5 5-5 M5 20h14" /></svg>SVG
            </button>
          </div>
        </div>
      </div>

      {/* 우측: 캔버스 */}
      <div className="design-main ckd-main">
        <div className="ckd-toolbar">
          <div className="ckd-tg">
            <button type="button" className={tool === "select" ? "on" : ""} onClick={() => setTool("select")} title={t("design.toolSelect")}>
              <svg viewBox="0 0 24 24"><path d="M4 3l7 17 2.5-7.5L21 10z" /></svg>{t("designUi.toolSelectShort")}
            </button>
            <button type="button" className={tool === "node" ? "on" : ""} onClick={() => setTool("node")} title={t("design.toolNode")}>
              <svg viewBox="0 0 24 24"><path d="M4 18c4-10 12-10 16 0" /><rect x="2.5" y="16.5" width="3.5" height="3.5" /><rect x="18" y="16.5" width="3.5" height="3.5" /><circle cx="12" cy="10.5" r="2" /></svg>{t("designUi.toolNodeShort")}
            </button>
            <button type="button" className="ic" onClick={selectAll} title={t("design.toolSelectAll")}>
              <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3 2" /></svg>
            </button>
          </div>
          <div className="ckd-tg">
            {[
              ["pen", "toolPen", <path key="p" d="M3 21c3-1 5-2 6-4l9-9-2-2-9 9c-2 1-3 3-4 6z M14 6l4 4" />],
              ["line", "toolLine", <path key="l" d="M5 19L19 5" />],
              ["circle", "toolCircle", <circle key="c" cx="12" cy="12" r="8" />],
              ["rect", "toolRect", <rect key="r" x="4" y="5" width="16" height="14" rx="2" />],
              ["eraser", "toolEraser", <path key="e" d="M7 21h12 M5 15l9-9 5 5-8 8H8z" />],
            ].map(([k, nameKey, icon]) => (
              <button type="button" key={k} className={`ic ${tool === k ? "on" : ""}`} onClick={() => setTool(k)} title={t(`design.${nameKey}`)}>
                <svg viewBox="0 0 24 24">{icon}</svg>
              </button>
            ))}
          </div>
          <div className="ckd-tg">
            <button type="button" className="ic" onClick={undo} title="Undo (Ctrl+Z)"><svg viewBox="0 0 24 24"><path d="M9 14L4 9l5-5 M4 9h11a5 5 0 0 1 0 10h-3" /></svg></button>
            <button type="button" className="ic" onClick={redo} title="Redo (Ctrl+Shift+Z)"><svg viewBox="0 0 24 24"><path d="M15 14l5-5-5-5 M20 9H9a5 5 0 0 0 0 10h3" /></svg></button>
          </div>
          <span className="ckd-sp" />
          <div className="ckd-tg">
            <button type="button" className="ic" onClick={zoomOut} title={t("design.toolZoomOut")}><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4 M8 11h6" /></svg></button>
            <button type="button" className="ic" onClick={zoomIn} title={t("design.toolZoomIn")}><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4 M11 8v6 M8 11h6" /></svg></button>
            <button type="button" className="ic" onClick={zoomReset} title={t("design.toolZoomReset")}><svg viewBox="0 0 24 24"><path d="M4 9V4h5 M20 9V4h-5 M4 15v5h5 M20 15v5h-5" /></svg></button>
          </div>
          <div className="ckd-tg">
            <button type="button" className="ic danger" onClick={clearCanvas} title={t("design.toolClear")}><svg viewBox="0 0 24 24"><path d="M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13" /></svg></button>
          </div>
        </div>

        {/* 선 굵기 (그리기 도구 선택 시) */}
        {isDrawTool && (
          <div className="design-subbar ckd-subbar">
            <label>{t("design.strokeWidth")}</label>
            <input
              type="range"
              min="2"
              max="30"
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
            />
            <span>{strokeWidth}px</span>
            <div className="design-stroke-preview">
              <div
                className="design-stroke-preview-line"
                style={{ height: strokeWidth, borderRadius: strokeWidth / 2 }}
              />
            </div>
          </div>
        )}

        {/* 이미 그려진 그림의 굵기 조절 (선택/노드 도구) */}
        {!isDrawTool && hasContent && (
          <div className="design-subbar ckd-subbar">
            <label>{t("designUi.thickness")}</label>
            <input
              type="range"
              min="-20"
              max="20"
              value={thicknessDelta}
              onPointerDown={beginThicknessEdit}
              onKeyDown={beginThicknessEdit}
              onPointerUp={endThicknessEdit}
              onKeyUp={endThicknessEdit}
              onBlur={endThicknessEdit}
              onChange={(e) => {
                const value = Number(e.target.value);
                setThicknessDelta(value);
                applyThickness(value);
              }}
            />
            <span>{thicknessDelta > 0 ? `+${thicknessDelta}` : thicknessDelta}px</span>
            <span className="design-subbar-hint">{t("design.thicknessHint")}</span>
            <button
              className="design-subbar-btn"
              onClick={resetThickness}
              disabled={thicknessDelta === 0}
            >
              {t("design.thicknessReset")}
            </button>
          </div>
        )}

        {/* 캔버스 */}
        <div className="design-canvas-wrap ckd-paperwrap">
          <canvas
            ref={canvasRef}
            className="design-canvas"
            tabIndex={0}
            onMouseDown={() => canvasRef.current?.focus()}
          />
          {!hasContent && !generating && !converting && !introDismissed && (
            <div className="ckd-intro">
              <h3>{t("designUi.introTitle")}</h3>
              <p>{t("designUi.introSub")}</p>
              <div className="ckd-intro-two">
                <button type="button" className="pri" onClick={() => { setIntroDismissed(true); switchDrawMode("ai"); setTimeout(() => promptRef.current?.focus(), 0); }}>
                  ✨<b>{t("designUi.introAi")}</b><small>{t("designUi.introAiSub")}</small>
                </button>
                <button type="button" onClick={() => { setIntroDismissed(true); switchDrawMode("hand"); }}>
                  ✏️<b>{t("designUi.introHand")}</b><small>{t("designUi.introHandSub")}</small>
                </button>
              </div>
            </div>
          )}
          {saveMsg && <div className="design-toast">{saveMsg}</div>}
          {(generating || converting) && (
            <div className="design-loading-overlay">
              <div className="design-loading-cookie">
                <span className="cookie-icon">🍪</span>
              </div>
              <div className="design-loading-text">
                {generating ? t("design.generatingStatus") : t("design.convertingSvg")}
              </div>
              <div className="design-loading-dots">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 저장 모달 */}
      {saveModal.open && (
        <div className="design-modal-overlay" onClick={() => setSaveModal({ open: false, name: "" })}>
          <div className="design-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("design.saveNamePrompt")}</h3>
            <input
              type="text"
              value={saveModal.name}
              onChange={(e) => setSaveModal((prev) => ({ ...prev, name: e.target.value }))}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && saveModal.name.trim()) {
                  setSaveModal({ open: false, name: "" });
                  saveDesign(saveModal.name);
                }
              }}
            />
            <div className="design-modal-actions">
              <button onClick={() => setSaveModal({ open: false, name: "" })}>
                {t("design.cancel")}
              </button>
              <button
                className="primary"
                disabled={!saveModal.name.trim()}
                onClick={() => {
                  setSaveModal({ open: false, name: "" });
                  saveDesign(saveModal.name);
                }}
              >
                {t("design.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이름 변경 모달 */}
      {renameModal.open && (
        <div className="design-modal-overlay" onClick={() => setRenameModal({ open: false, id: null, name: "" })}>
          <div className="design-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("design.renamePrompt")}</h3>
            <input
              type="text"
              value={renameModal.name}
              onChange={(e) => setRenameModal((prev) => ({ ...prev, name: e.target.value }))}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && renameModal.name.trim()) {
                  renameDesign(renameModal.id, renameModal.name);
                  setRenameModal({ open: false, id: null, name: "" });
                }
              }}
            />
            <div className="design-modal-actions">
              <button onClick={() => setRenameModal({ open: false, id: null, name: "" })}>
                {t("design.cancel")}
              </button>
              <button
                className="primary"
                disabled={!renameModal.name.trim()}
                onClick={() => {
                  renameDesign(renameModal.id, renameModal.name);
                  setRenameModal({ open: false, id: null, name: "" });
                }}
              >
                {t("design.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
